import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
export function DiagnosticCTA() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    sector: '',
    website: '',
    email: '',
    challenge: ''
  });
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const response = await fetch('https://formspree.io/f/xjgdkoor', {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: new FormData(e.currentTarget)
      });
      if (response.ok) {
        setIsSubmitted(true);
      } else {
        setError('Hubo un problema al enviar tu solicitud. Intenta de nuevo en unos minutos.');
      }
    } catch {
      setError('No pudimos enviar tu solicitud. Verifica tu conexión e intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const inputClass = 'bg-transparent border border-arena/20 rounded-[6px] px-4 py-[14px] font-body text-[15px] text-arena placeholder:text-arena/35 focus:outline-none focus:border-arena/60 transition-colors duration-200 mb-4';
  return <section className="bg-verde text-arena py-[72px] md:py-[120px]" data-id="element-120">
      <div className="max-w-[1200px] mx-auto px-6 md:px-20" data-id="element-121">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.4,
        ease: 'easeOut'
      }} className="mb-12 md:mb-[64px]" data-id="element-122">
          <span className="block font-body text-[11px] uppercase font-medium text-arena/50 tracking-widest mb-4" data-id="element-123">
            DIAGNÓSTICO DE TU SITIO WEB
          </span>
          <h2 className="font-display text-[32px] md:text-[48px] font-bold text-arena leading-[1.1] max-w-[560px] mb-6" data-id="element-124">
            Descubre qué está fallando en tu sitio web.
          </h2>
          <p className="font-body text-[16px] md:text-[20px] text-arena/70 leading-[1.6] max-w-[480px]" data-id="element-125">
            En 48 horas recibirás el análisis completo de tu sitio web en las
            cinco dimensiones del ICD™, con las brechas priorizadas y
            recomendaciones específicas.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24" data-id="element-126">
          {/* Left Column */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.4,
          ease: 'easeOut',
          delay: 0.1
        }} data-id="element-127">
            <h3 className="font-display text-[20px] font-semibold text-arena mb-6" data-id="element-128">
              El diagnóstico incluye:
            </h3>

            <div className="space-y-4 mb-8" data-id="element-129">
              {['ICD™ de tu sitio web (puntuación 0-100)', 'Qué está fallando en cada una de las 5 dimensiones', 'Las 3 correcciones de mayor impacto para tu sitio', 'Recomendaciones específicas para tu sitio web', 'Cómo se compara tu sitio con el promedio del sector'].map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-130">
                  <span className="text-salvia mt-1" data-id="element-131">→</span>
                  <p className="font-body text-[16px] text-arena/80 leading-[1.6]" data-id="element-132">
                    {item}
                  </p>
                </div>)}
            </div>

            <div className="h-px w-full bg-arena/15 mb-6" data-id="element-133"></div>

            <div className="flex flex-col gap-1 mb-8" data-id="element-134">
              <span className="font-body text-[14px] text-arena/50 line-through" data-id="element-135">
                Valor: $500.000 COP
              </span>
              <span className="font-body text-[16px] font-semibold text-arena" data-id="element-136">
                Sin costo en esta etapa
              </span>
            </div>

            <div className="h-px w-full bg-arena/15 mb-8" data-id="element-137"></div>

            <h4 className="font-display text-[16px] font-semibold text-arena mb-3" data-id="element-138">
              ¿Por qué es gratuito?
            </h4>
            <p className="font-body text-[15px] text-arena/65 leading-[1.6] mb-4" data-id="element-139">
              Porque creemos que cuando veas exactamente qué está fallando en tu
              sitio web, querrás corregirlo. El diagnóstico es nuestra forma de
              demostrarte cómo lo hacemos.
            </p>

            <div className="h-px w-full bg-arena/15 my-8" data-id="element-140"></div>

            <h4 className="font-display text-[14px] font-semibold text-arena mb-3" data-id="element-141">
              Cómo es diferente.
            </h4>
            <p className="font-body text-[14px] text-arena/60 leading-[1.6]" data-id="element-142">
              Cada diagnóstico lo realiza el equipo de Origen Studio
              manualmente. No hay plantillas, no hay automatizaciones, no hay
              respuestas genéricas. Analizamos tu sitio web específico con el
              ICD™ y entregamos hallazgos que aplican a tu empresa, no a un
              sector en abstracto.
            </p>
          </motion.div>

          {/* Right Column - Form */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.4,
          ease: 'easeOut',
          delay: 0.2
        }} data-id="element-143">
            {!isSubmitted ? <div className="bg-arena/5 border border-arena/15 rounded-[12px] p-6 md:p-10" data-id="element-144">
                <p className="font-body text-[13px] text-arena/50 mb-6 text-center" data-id="element-145">
                  6 campos · Menos de 2 minutos
                </p>
                <form onSubmit={handleSubmit} action="https://formspree.io/f/xjgdkoor" method="POST" className="flex flex-col" data-id="element-146">
                  <input type="text" name="name" placeholder="Tu nombre completo" required onChange={handleChange} className={inputClass} data-id="element-147" />
                  <input type="text" name="company" placeholder="Nombre de la empresa" required onChange={handleChange} className={inputClass} data-id="element-148" />
                  <select name="sector" required defaultValue="" onChange={handleChange} className={`${inputClass} appearance-none ${formData.sector ? 'text-arena' : 'text-arena/35'}`} data-id="element-149">
                    <option value="" disabled data-id="element-150">
                      Selecciona tu sector
                    </option>
                    <option value="Firma Legal" className="text-carbon" data-id="element-151">
                      Firma Legal
                    </option>
                    <option value="Logística y Transporte" className="text-carbon" data-id="element-152">
                      Logística y Transporte
                    </option>
                    <option value="Comercio Exterior" className="text-carbon" data-id="element-153">
                      Comercio Exterior
                    </option>
                    <option value="Consultoría" className="text-carbon" data-id="element-154">
                      Consultoría
                    </option>
                    <option value="Servicios Profesionales" className="text-carbon" data-id="element-155">
                      Servicios Profesionales
                    </option>
                    <option value="Otro sector B2B" className="text-carbon" data-id="element-156">
                      Otro sector B2B
                    </option>
                  </select>
                  <input type="text" name="website" placeholder="www.tuempresa.com (si ya tienes sitio web)" onChange={handleChange} className={inputClass} data-id="element-157" />
                  <input type="email" name="email" placeholder="correo@tuempresa.com" required onChange={handleChange} className={inputClass} data-id="element-158" />

                  <select name="challenge" required defaultValue="" onChange={handleChange} className={`${inputClass} appearance-none ${formData.challenge ? 'text-arena' : 'text-arena/35'}`} data-id="element-159">
                    <option value="" disabled data-id="element-160">
                      ¿Cuál es tu mayor desafío digital?
                    </option>
                    <option value="Generar más consultas de potenciales clientes" className="text-carbon" data-id="element-161">
                      Generar más consultas de potenciales clientes
                    </option>
                    <option value="Mejorar la imagen y credibilidad de la empresa" className="text-carbon" data-id="element-162">
                      Mejorar la imagen y credibilidad de la empresa
                    </option>
                    <option value="Diferenciarnos de la competencia en digital" className="text-carbon" data-id="element-163">
                      Diferenciarnos de la competencia en digital
                    </option>
                    <option value="Convertir más visitas en clientes reales" className="text-carbon" data-id="element-164">
                      Convertir más visitas en clientes reales
                    </option>
                    <option value="No sé exactamente, por eso quiero el diagnóstico" className="text-carbon" data-id="element-165">
                      No sé exactamente, por eso quiero el diagnóstico
                    </option>
                  </select>

                  <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center bg-arena text-verde font-display font-bold text-[15px] px-8 py-4 rounded-[6px] hover:bg-[#EAE7E0] transition-colors duration-200 mt-2 mb-4 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-166">
                    {isSubmitting ? 'Enviando...' : <>
                        Iniciar diagnóstico de mi sitio web{' '}
                        <ArrowRight size={16} className="ml-2" data-id="element-167" />
                      </>}
                  </button>

                  {error && <p role="alert" className="font-body text-[13px] text-[#E85555] text-center mb-3" data-id="element-168">
                      {error}
                    </p>}

                  <p className="font-body text-[12px] text-arena/40 text-center leading-relaxed mt-4" data-id="element-169">
                    Sin compromiso · Sin reunión previa · 48 horas
                    <br data-id="element-170" />
                    El análisis lo realiza personalmente el equipo de Origen
                    Studio. No un algoritmo automático.
                  </p>
                </form>
              </div> : <div className="border border-arena/15 rounded-[12px] p-12 text-center" style={{
            backgroundColor: 'rgba(247,244,237,0.08)'
          }} data-id="element-171">
                <h3 className="font-display text-[28px] font-bold text-arena mb-4" data-id="element-172">
                  Tu solicitud fue recibida.
                </h3>
                <p className="font-body text-[18px] text-arena/70 leading-[1.6]" data-id="element-173">
                  Analizaremos tu sitio web en las próximas 48 horas hábiles y
                  enviaremos el diagnóstico a tu correo.
                </p>
              </div>}
          </motion.div>
        </div>
      </div>
    </section>;
}